import React, { useState, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Camera, UploadSimple, User as UserIcon } from "@phosphor-icons/react";

export default function AvatarUploader({ size = 96, className = "" }) {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(null);

  if (!user) return null;

  const onPick = () => inputRef.current?.click();

  const onChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Pick an image (JPG/PNG/WEBP/GIF)");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Max 5 MB");
      return;
    }
    await doUpload(file, false);
  };

  const doUpload = async (file, force) => {
    const objectUrl = URL.createObjectURL(file);
    setPreview(objectUrl);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/users/profile-picture${force ? "?force=true" : ""}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await refresh();
      toast.success("Profile picture updated");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Structured "no face" error → offer an override toast.
      if (detail && typeof detail === "object" && detail.error_code === "no_face") {
        toast.error(detail.message || "That doesn't look like a face photo.", {
          duration: 12000,
          action: {
            label: "Upload anyway",
            onClick: () => doUpload(file, true),
          },
        });
      } else {
        toast.error((typeof detail === "string" ? detail : detail?.message) || "Upload failed");
      }
    } finally {
      URL.revokeObjectURL(objectUrl);
      setPreview(null);
      setBusy(false);
    }
  };

  const src = preview || user.picture;

  return (
    <div className={`flex items-center gap-5 ${className}`} data-testid="avatar-uploader">
      <div
        className="relative border-2 border-foreground overflow-hidden bg-secondary shrink-0"
        style={{ width: size, height: size }}
      >
        {src ? (
          <img
            src={src}
            alt="profile"
            referrerPolicy="no-referrer"
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center font-display text-3xl">
            {(user.name || "U")[0]?.toUpperCase()}
          </div>
        )}
        {busy && (
          <div className="absolute inset-0 bg-foreground/60 text-background font-mono text-[10px] uppercase tracking-wider flex items-center justify-center">
            Uploading…
          </div>
        )}
      </div>
      <div className="flex-1">
        <div className="font-display text-xl tracking-tighter">{user.name || "Your photo"}</div>
        <div className="text-xs text-foreground/60">JPG · PNG · WEBP · up to 5 MB</div>
        <input
          ref={inputRef}
          data-testid="avatar-file-input"
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          className="hidden"
          onChange={onChange}
        />
        <Button
          data-testid="avatar-upload-btn"
          onClick={onPick}
          disabled={busy}
          className="mt-2 rounded-none bg-foreground text-background hover:bg-foreground/90 disabled:opacity-50"
          size="sm"
        >
          <Camera size={16} weight="fill" /> Change photo
        </Button>
      </div>
    </div>
  );
}
